import React from 'react'
import { Check } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import styled from '@emotion/styled'

const MAX_STEPS = 6
const COLOR_COMPLETE = '#BB99FD'
const COLOR_OTHER = '#BEBEBE'
const COLOR_OTHER_LABEL = '#6F6F6F'

const ProgressLabel = styled.div`
  text-align: center;
  font-variant-numeric: lining-nums proportional-nums;
  font-family: 'Fira Sans';
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  padding: 4px 0px;
  width: 130px;
`

interface ChroniquerProgressBarProps {
  currentStep: number | null
  completedSteps: number[]
}

const ChroniquerProgressBar: React.FC<ChroniquerProgressBarProps> = ({
  currentStep,
  completedSteps = [],
}) => {
  const { t } = useTranslation()

  const progressPercentage = currentStep ? (currentStep / MAX_STEPS) * 100 : 100

  return (
    <div className="w-fit px-4 mx-auto">
      <div className="relative flex items-center">
        <div
          className="absolute top-4 left-0 h-px w-full -translate-y-1/2"
          style={{ backgroundColor: COLOR_OTHER }}
        />
        <div
          className="absolute top-4 left-0 h-px -translate-y-1/2 transition-all duration-500"
          style={{
            width: `${progressPercentage}%`,
            backgroundColor: COLOR_COMPLETE,
          }}
        />

        <div className="relative flex w-auto justify-between">
          {Array.from({ length: MAX_STEPS }, (_, index) => {
            const stepNumber = index + 1
            const isCompleted = completedSteps.includes(stepNumber)
            const isForgotten = currentStep
              ? currentStep > stepNumber && !isCompleted
              : !isCompleted
            const isCurrent = stepNumber === currentStep

            let circleStyle = {
              borderColor: COLOR_OTHER,
              backgroundColor: 'white',
            }
            let labelColor = COLOR_OTHER_LABEL

            if (isCompleted || isCurrent || isForgotten) {
              circleStyle = {
                backgroundColor: COLOR_COMPLETE,
                borderColor: COLOR_COMPLETE,
              }
              labelColor = COLOR_COMPLETE
            }

            return (
              <div
                key={index}
                className="z-10 flex flex-col items-center text-center w-[160px]"
              >
                <div
                  className="flex h-8 w-8 items-center justify-center rounded-full border-2 transition-all duration-500"
                  style={circleStyle}
                >
                  {isCompleted ? (
                    <Check className="h-5 w-5 text-white" />
                  ) : (
                    <span
                      className="text-sm font-semibold"
                      style={{
                        color:
                          isCurrent || isForgotten
                            ? 'white'
                            : COLOR_OTHER_LABEL,
                      }}
                    >
                      {stepNumber}
                    </span>
                  )}
                </div>
                <ProgressLabel
                  style={{
                    color: labelColor,
                  }}
                >
                  {t(`chroniqueurView.progressBar.${stepNumber}`)}
                </ProgressLabel>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default ChroniquerProgressBar
